// Which plantings (farm_projects) are live on a given date — the ONE exported
// TypeScript home for the date-overlap predicate that already exists three
// times, consistently, elsewhere:
//   • SQL, ha-weighted:      block_committed_ha()            (mig 735)
//   • SQL, period-weighted:  project_active_weeks_in_period() (mig 531)
//   • TS, BlockPlantings.tsx committedHa()/inUseHa
// Capture screens (SprayRecords, SprayTasks — spray-planting-target spec,
// Phase B) consume this module instead of growing a fourth copy; refactoring
// BlockPlantings onto it is the spec's recorded follow-up. Semantics match
// the SQL predicate exactly: only the FINISHED statuses free the land
// ('done' plantings still count when the date falls inside their window —
// a backdated spray on a since-closed planting is still that planting's).
//
// Dates are 'YYYY-MM-DD' strings (PostgREST DATE columns); lexical compare
// is correct for that shape.

/** Statuses that never occupy land (mirrors BlockPlantings FINISHED + the
 * mig 735/740 `status NOT IN (…)` list). */
export const PLANTING_FINISHED_STATUSES = ['cancelled', 'rotten', 'not_germinated'] as const

export interface PlantingWindowFields {
  status: string
  cycle_start_date: string
  expected_end_date: string | null
  actual_end_date: string | null
}

/** Live on `ymd`: not a FINISHED status, started on or before `ymd`, and not
 * ended before it — actual end wins over expected; no end date = open. */
export function plantingActiveOn(p: PlantingWindowFields, ymd: string): boolean {
  if ((PLANTING_FINISHED_STATUSES as readonly string[]).includes(p.status)) return false
  if (!p.cycle_start_date || p.cycle_start_date > ymd) return false
  const end = p.actual_end_date ?? p.expected_end_date
  return end == null || end >= ymd
}

/** Hectares IN THE GROUND on `ymd` — the plantings panel's "ha in use"
 *  (BlockPlantings) and the Farm Map's pivot panel (FM01 UF-11) read this ONE
 *  rule. Active/planning windows covering the day; a FINISHED or `done`
 *  planting holds no land; no size, no claim. NOT a cross-window sum —
 *  replanted or harvested cycles don't stack (a sequentially replanted 17 ha
 *  pivot must not read as 68/17). */
export function plantingsInUseHa(
  plantings: readonly (PlantingWindowFields & { size_ha: number | string | null })[],
  ymd: string,
): number {
  let t = 0
  for (const p of plantings) {
    if ((PLANTING_FINISHED_STATUSES as readonly string[]).includes(p.status) || p.status === 'done' || p.size_ha == null) continue
    const end = p.actual_end_date || p.expected_end_date || '9999-12-31'
    if (p.cycle_start_date <= ymd && end >= ymd) t += Number(p.size_ha)
  }
  return t
}

/** The live plantings on `ymd`, newest cycle first (picker order). */
export function activePlantingsOn<T extends PlantingWindowFields>(plantings: T[], ymd: string): T[] {
  return plantings
    .filter(p => plantingActiveOn(p, ymd))
    .sort((a, b) => (a.cycle_start_date < b.cycle_start_date ? 1 : a.cycle_start_date > b.cycle_start_date ? -1 : 0))
}

/**
 * The block's SOLE live planting on `ymd`, or null — the pickerless
 * (QuickLog) twin of usePlantingPicker's pristine auto-select: exactly one
 * live planting is the only scope a formless rapid capture can honestly
 * claim; zero or two-plus → whole block. `plantings` may be undefined (an
 * unloaded/cold offline cache) — that also answers null, i.e. whole block,
 * the conservative degrade every guarded table shares. If this rule ever
 * changes, change the hook's auto-select in the same commit.
 */
export function soleLivePlantingIdOn<T extends PlantingWindowFields & { block_id: string | null; project_id: string }>(
  plantings: T[] | undefined, blockId: string, ymd: string,
): string | null {
  const live = (plantings ?? []).filter(p => p.block_id === blockId && plantingActiveOn(p, ymd))
  return live.length === 1 ? live[0].project_id : null
}

/**
 * The B3 fan-out rule for project_id, named once: a picked planting belongs
 * to the PRIMARY block only — "also apply/plan for" sibling rows save whole
 * block (each block has its own plantings; a carried-over id would just
 * degrade server-side with a warning per row). '' normalises to null.
 */
export function primaryOnlyProjectId(bid: string, primaryBlockId: string, projectId: string): string | null {
  return bid === primaryBlockId ? (projectId || null) : null
}

/**
 * A usable hectare figure (planted size or block area), or null. Coerces via
 * Number() —
 * hectare-ish columns surface as strings through some JSON paths (the
 * callers already Number()-wrap block hectares for the same reason), and a
 * silent typeof-mismatch here would quietly fall back to whole-block
 * dosing while the UI claims otherwise.
 */
export function usableHa(sizeHa: number | string | null | undefined): number | null {
  if (sizeHa == null || sizeHa === '') return null
  const n = Number(sizeHa)
  return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * The per-hectare dosing denominator for one targeted block: the planting's
 * exact planted hectares when a planting is chosen and carries a usable
 * size_ha, else the block's hectares (mig 521: size_ha is "exact ha planted
 * (NOT block ha when pivot half-planted)"). Null when neither side knows —
 * the mix maths already treats null as "no per-ha basis".
 */
export function plantingDosingHa(
  sizeHa: number | string | null | undefined,
  blockHectares: number | string | null | undefined,
): number | null {
  return usableHa(sizeHa) ?? usableHa(blockHectares)
}

/**
 * The ONE loop rule for a spray's total targeted hectares (spec B3): the
 * PRIMARY block doses off the selected planting's planted hectares. An extra
 * covered block doses off ITS OWN planting's planted hectares when the caller
 * names one in `extraPlantingHa` (block_id -> that planting's size_ha; mig
 * 20261047700000), else off the whole block. Shared by the SprayRecords
 * capture form, the SprayTasks plan preview and the MySprayTasks completion
 * sheet so plan, capture and execution can never quote different areas for
 * the same pass (grouped passes included) — the last two omit the 4th
 * argument: a task stores one planting, for its primary block, so a grouped
 * task's extras are quoted whole-block (completion seeds them the same way).
 *
 * `blocks` = the targeted set (primary + extras), each with its block
 * hectares; null when no targeted block has a usable area.
 */
export function sprayTargetHa(
  blocks: Array<{ block_id: string; hectares: number | string | null | undefined }>,
  primaryBlockId: string,
  plantingSizeHa: number | string | null | undefined,
  extraPlantingHa?: ReadonlyMap<string, number | string | null | undefined> | Record<string, number | string | null | undefined>,
): number | null {
  const extraHa = (id: string) =>
    extraPlantingHa instanceof Map ? extraPlantingHa.get(id)
      : extraPlantingHa ? (extraPlantingHa as Record<string, number | string | null | undefined>)[id]
      : undefined
  let sum = 0
  let any = false
  const seen = new Set<string>()
  for (const b of blocks) {
    if (!b.block_id || seen.has(b.block_id)) continue
    seen.add(b.block_id)
    const ha = b.block_id === primaryBlockId
      ? plantingDosingHa(plantingSizeHa, b.hectares)
      : plantingDosingHa(extraHa(b.block_id), b.hectares)
    if (ha != null) { sum += ha; any = true }
  }
  return any ? sum : null
}

/** '' is the UI select's whole-block sentinel — treat it as null everywhere
 * so a raw picker value can never read as an explicit planting (a '' pick
 * vs a scoped spray would otherwise silently DROP the hold — an
 * under-warning on a food-safety advisor). */
function normPlanting(v: string | null | undefined): string | null {
  return v == null || v === '' ? null : v
}

/** Two plantings' windows overlap in time (open end = ∞). Concurrent
 * plantings partition a block in SPACE (carrots on one half, beetroot on
 * the other); sequential ones partition it in TIME — same ground. */
export function plantingWindowsOverlap(a: PlantingWindowFields, b: PlantingWindowFields): boolean {
  const endA = a.actual_end_date ?? a.expected_end_date
  const endB = b.actual_end_date ?? b.expected_end_date
  return (endA == null || b.cycle_start_date <= endA)
      && (endB == null || a.cycle_start_date <= endB)
}

/**
 * The ONE planting-scope rule both PHI directions share (Phase C,
 * harvest-planting-phi spec): does a spray's hold apply to a pick — equally,
 * does a pick anchor a spray's harvest window?
 *
 * TRUE (hold applies) unless BOTH sides name plantings that provably occupy
 * DIFFERENT GROUND: different ids whose windows OVERLAP in time (concurrent
 * = spatial split of the block). Everything else is conservative:
 *   • either side whole-block (NULL/'') → applies;
 *   • same planting → applies;
 *   • no window data, or either planting unknown → applies;
 *   • DISJOINT windows → applies — sequential cycles (perennial seasons,
 *     replants) are the SAME ground later in time, and the residue is on
 *     the ground, not the paperwork (a spray attributed to cycle-2026 must
 *     still hold a pick attributed to cycle-2027 on the same trees).
 */
export function sprayHoldApplies(
  sprayProjectId: string | null | undefined,
  pickProjectId: string | null | undefined,
  windows: ReadonlyMap<string, PlantingWindowFields> | null | undefined,
): boolean {
  const a = normPlanting(sprayProjectId)
  const b = normPlanting(pickProjectId)
  if (a == null || b == null || a === b) return true
  const wa = windows?.get(a)
  const wb = windows?.get(b)
  if (!wa || !wb) return true
  return !plantingWindowsOverlap(wa, wb)
}

/**
 * Harvest attribution window: a pick may still be attributed to a planting
 * for `graceDays` after its window closed — cleanup/backfilled picks land
 * days after the close that the final pick itself usually caused. Sprays
 * use plantingActiveOn directly (you don't spray a removed crop).
 */
export function plantingAttributableOn(
  p: PlantingWindowFields,
  ymd: string,
  graceDays: number,
): boolean {
  if (plantingActiveOn(p, ymd)) return true
  if (graceDays <= 0) return false
  if ((PLANTING_FINISHED_STATUSES as readonly string[]).includes(p.status)) return false
  if (!p.cycle_start_date || p.cycle_start_date > ymd) return false
  const end = p.actual_end_date ?? p.expected_end_date
  if (end == null || end >= ymd) return false
  const [y, m, d] = end.split('-').map(Number)
  const graced = new Date(Date.UTC(y, m - 1, d + graceDays)).toISOString().slice(0, 10)
  return graced >= ymd
}

/**
 * The one planting-degrade message (spec Phase B contract: never silent,
 * one voice across live saves, edits and queue drains) — per record type,
 * because every guarded table says it in the same words about a different
 * thing. `noun` is what the operator just saved, lower case.
 *
 * It used to be a constant hardcoding 'the spray', which the harvest-pick
 * call sites reused verbatim: a degraded PICK told the operator a SPRAY
 * had been recorded against the whole block — a record they had not just
 * saved, sending them to the wrong register, while the pick's own scope
 * change went unnamed. Live on prod from Phase C (2026-09-03) until this.
 */
export function plantingDegradedMessage(noun: string): string {
  return `The picked planting no longer matches this block, so the ${noun} was `
    + 'recorded against the WHOLE BLOCK. Check the planting under Orchard, '
    + 'then edit the record if it should be planting-specific.'
}

/** The spray wording — unchanged, so no spray call site or pin moves. */
export const PLANTING_DEGRADED_MESSAGE = plantingDegradedMessage('spray')

// ── Status moves (FM01 UF-23) ───────────────────────────────────────────────
// The statuses a planting may move to, mirroring the farm_projects status
// trigger (mig 521, farm_projects_check_status_transition): planning goes to
// active or cancelled; active goes to a finished status; a finished planting
// goes nowhere, except for a platform admin (support), who may leave it.
// If the trigger changes, change this and plantingMovesFrom's test together.
export type PlantingStatus = 'planning' | 'active' | 'done' | 'cancelled' | 'rotten' | 'not_germinated'
const FINISHED_MOVES: PlantingStatus[] = ['done', 'cancelled', 'rotten', 'not_germinated']
const ALL_STATUSES: PlantingStatus[] = ['planning', 'active', ...FINISHED_MOVES]

/** Statuses a planting can move to from `from` (never `from` itself). */
export function plantingMovesFrom(from: string, isPlatformAdmin: boolean): PlantingStatus[] {
  if (from === 'planning') return ['active', 'cancelled']
  if (from === 'active') return FINISHED_MOVES
  if ((FINISHED_MOVES as string[]).includes(from)) {
    return isPlatformAdmin ? ALL_STATUSES.filter(s => s !== from) : []
  }
  return []
}

/** Ending a cycle (it gets an end date and cannot be reopened by the farm). */
export const isPlantingEnd = (s: string): boolean => (FINISHED_MOVES as string[]).includes(s)
